import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  Stack,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import {
  useGetServiceDropdownQuery,
  useGetAllApiKeysQuery,
  useDeleteApiKeyMutation,
  useVerifyMpinMutation,
  useSoftDeleteApiKeyMutation,
  useRevokeApiKeyMutation,
  useGenerateApiKeyNewMutation,
} from "../store/services/userApi";

// Action config: label shown in the MPIN dialog + button styling per action type
const ACTION_CONFIG = {
  delete: {
    label: "Delete",
    buttonColor: "error",
    confirmText: "delete this API key",
  },
  revoke: {
    label: "Revoke",
    buttonColor: "warning",
    confirmText: "revoke this API key",
  },
  permanentDelete: {
    label: "Permanently Delete",
    buttonColor: "error",
    confirmText: "permanently delete this API key. This action cannot be undone",
  },
  viewKey: {
    label: "View API Key",
    buttonColor: "primary",
    confirmText: "view this API key",
  },
};

const ManageApiKey = () => {
  // ── Service dropdown (for choosing what a new key is generated for) ────
  const { data: functionsResp, isLoading: functionsLoading } = useGetServiceDropdownQuery();
  const functionOptions = functionsResp?.data ?? [];

  // ── Existing API keys (active + inactive derived from `status`) ────────
  const {
    data: apiKeysResp,
    isLoading: keysLoading,
    isFetching: keysFetching,
    refetch: refetchApiKeys,
  } = useGetAllApiKeysQuery();

  // Response shape for /apikey/get-all-apikey (confirmed):
  //   { apikeys_id, apikey, is_active, function: { function_id, function_name } }
  const allKeys = apiKeysResp?.data ?? [];

  const activeKeys = useMemo(() => allKeys.filter((k) => k.is_active), [allKeys]);
  const inactiveKeys = useMemo(() => allKeys.filter((k) => !k.is_active), [allKeys]);

  // ── Mutations ────────────────────────────────────────────────────────
  const [generateApiKeyNew, { isLoading: isGenerating }] = useGenerateApiKeyNewMutation();
  const [verifyMpin, { isLoading: isVerifying }] = useVerifyMpinMutation();
  const [softDeleteApiKey, { isLoading: isSoftDeleting }] = useSoftDeleteApiKeyMutation();
  const [deleteApiKey, { isLoading: isDeleting }] = useDeleteApiKeyMutation();
  const [revokeApiKey, { isLoading: isRevoking }] = useRevokeApiKeyMutation();

  const isActionBusy = isVerifying || isSoftDeleting || isDeleting || isRevoking;

  // ── Generate flow ────────────────────────────────────────────────────
  const [selectedFunctionId, setSelectedFunctionId] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [newlyGeneratedKey, setNewlyGeneratedKey] = useState("");

  const selectedFunctionName = functionOptions.find(
    (f) => f.function_id === selectedFunctionId
  )?.function_name;

  // ── MPIN dialog state: { rowId, actionType } or null when closed ──────
  const [mpinDialog, setMpinDialog] = useState(null);
  const [mpin, setMpin] = useState("");
  const [mpinError, setMpinError] = useState("");

  // Delete-type chooser: holds the row id while the user picks
  // soft (temporary) vs permanent delete, before MPIN confirmation.
  const [deleteChoiceRowId, setDeleteChoiceRowId] = useState(null);

  // Holds the API key to display after a successful "viewKey" PIN check
  const [revealedKey, setRevealedKey] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  const handleCopy = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      showSnackbar("Copied to clipboard", "success");
    } catch {
      showSnackbar("Couldn't copy — please copy manually", "error");
    }
  };

  const handleGenerateKey = async () => {
    if (!selectedFunctionId) {
      showSnackbar("Please select a service first", "error");
      return;
    }
    try {
      const res = await generateApiKeyNew({ function_id: selectedFunctionId }).unwrap();

      // Field name confirmed as `apikey` elsewhere in this API (list +
      // verify-mpin) — kept the other fallbacks in case generate differs.
      const newKey = res?.data?.apikey || res?.data?.api_key || res?.api_key || res?.token || "";
      if (newKey) setNewlyGeneratedKey(newKey);

      showSnackbar(res?.message || "API Key generated successfully!", "success");
      setSelectedFunctionId("");
      refetchApiKeys();
    } catch (error) {
      showSnackbar(error?.data?.message || "Failed to generate API Key", "error");
    }
  };

  const openMpinDialog = (rowId, actionType) => {
    setMpin("");
    setMpinError("");
    setMpinDialog({ rowId, actionType });
  };

  const closeMpinDialog = () => {
    setMpinDialog(null);
    setMpin("");
    setMpinError("");
  };

  const openDeleteChoice = (rowId) => setDeleteChoiceRowId(rowId);
  const closeDeleteChoice = () => setDeleteChoiceRowId(null);

  const chooseDeleteType = (actionType) => {
    // actionType is "delete" (soft/temporary) or "permanentDelete"
    const rowId = deleteChoiceRowId;
    closeDeleteChoice();
    openMpinDialog(rowId, actionType);
  };

  const handleConfirmAction = async () => {
    if (!mpin || mpin.trim().length === 0) {
      setMpinError("MPIN is required");
      return;
    }

    const { rowId, actionType } = mpinDialog;

    try {
      // Every action here is gated behind MPIN verification first.
      const verifyRes = await verifyMpin({ apikeys_id: rowId, mpin }).unwrap();

      if (actionType === "delete") {
        await softDeleteApiKey({ apikeys_id: rowId }).unwrap();
        showSnackbar("API key deleted successfully!", "success");
        refetchApiKeys();
      }

      if (actionType === "revoke") {
        await revokeApiKey({ apikeys_id: rowId }).unwrap();
        showSnackbar("API key revoked successfully!", "success");
        refetchApiKeys();
      }

      if (actionType === "permanentDelete") {
        await deleteApiKey(rowId).unwrap();
        showSnackbar("API key permanently deleted!", "success");
        refetchApiKeys();
      }

      if (actionType === "viewKey") {
        // Confirmed field: verify-mpin returns the plaintext key as `apikey`.
        const key = verifyRes?.data?.apikey || "";
        setRevealedKey(key || "Unable to retrieve key");
      }

      closeMpinDialog();
    } catch (error) {
      setMpinError(error?.data?.message || "Invalid MPIN. Please try again.");
    }
  };

  const statusColor = (isActive) => (isActive ? "success" : "default");

  const currentConfig = mpinDialog ? ACTION_CONFIG[mpinDialog.actionType] : null;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        API Key Management
      </Typography>

      {/* Generate: pick a service, then generate */}
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel>Select Service</InputLabel>
          <Select
            label="Select Service"
            value={selectedFunctionId}
            onChange={(e) => setSelectedFunctionId(e.target.value)}
            disabled={functionsLoading}
          >
            {functionOptions.map((fn) => (
              <MenuItem key={fn.function_id} value={fn.function_id}>
                {fn.function_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          onClick={() => setConfirmOpen(true)}
          disabled={!selectedFunctionId || isGenerating}
        >
          {isGenerating ? (
            <CircularProgress size={22} color="inherit" />
          ) : (
            "Generate API Key"
          )}
        </Button>
      </Box>

      {/* Freshly generated key — shown once, dismissible */}
      {newlyGeneratedKey && (
        <Alert
          severity="success"
          sx={{ mt: 2 }}
          onClose={() => setNewlyGeneratedKey("")}
          action={
            <IconButton size="small" onClick={() => handleCopy(newlyGeneratedKey)}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          }
        >
          <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
            <strong>New API Key:</strong> {newlyGeneratedKey}
            <br />
            Copy it now — you won't be able to view it in plain text again.
          </Typography>
        </Alert>
      )}

      {/* Active Keys Table */}
      <Box mt={4}>
        <Typography variant="subtitle1" gutterBottom>
          Active API Keys
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table
            size="small"
            sx={{
              "& th": { borderRight: "1px solid #fff" },
              "& td": { borderRight: "1px solid #000" },
              "& td:last-child, & th:last-child": { borderRight: "none" },
            }}
          >
            <TableHead>
              <TableRow sx={{ backgroundColor: "#1565c0" }}>
                <TableCell sx={{ color: "#fff", fontWeight: 600 }}>Service</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 600 }} align="center">API_Key Value</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 600 }} align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {keysLoading && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <CircularProgress size={20} />
                  </TableCell>
                </TableRow>
              )}
              {!keysLoading && activeKeys.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No active API keys
                  </TableCell>
                </TableRow>
              )}
              {activeKeys.map((row) => (
                <TableRow key={row.apikeys_id}>
                  <TableCell>{row.function?.function_name || "—"}</TableCell>
                  <TableCell>
                    <Chip
                      label={row.is_active ? "Active" : "Inactive"}
                      color={statusColor(row.is_active)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="View API key">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => openMpinDialog(row.apikeys_id, "viewKey")}
                      >
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => openDeleteChoice(row.apikeys_id)}
                        sx={{ color: "error.main" }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Inactive Keys Table */}
      <Box mt={4}>
        <Typography variant="subtitle1" gutterBottom>
          Inactive API Keys
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table
            size="small"
            sx={{
              "& th": { borderRight: "1px solid #fff" },
              "& td": { borderRight: "1px solid #000" },
              "& td:last-child, & th:last-child": { borderRight: "none" },
            }}
          >
            <TableHead>
              <TableRow sx={{ backgroundColor: "#1565c0" }}>
                <TableCell sx={{ color: "#fff", fontWeight: 600 }}>Service</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 600 }} align="center">API_Key Value</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 600 }}>Permanent Delete On</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 600 }} align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {keysLoading && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <CircularProgress size={20} />
                  </TableCell>
                </TableRow>
              )}
              {!keysLoading && inactiveKeys.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No inactive API keys
                  </TableCell>
                </TableRow>
              )}
              {inactiveKeys.map((row) => (
                <TableRow key={row.apikeys_id}>
                  <TableCell>{row.function?.function_name || "—"}</TableCell>
                  <TableCell>
                    <Chip
                      label={row.is_active ? "Active" : "Inactive"}
                      color={statusColor(row.is_active)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="View API key">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => openMpinDialog(row.apikeys_id, "viewKey")}
                      >
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{row.permanent_delete_on || "—"}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Tooltip title="Revoke deletion">
                        <IconButton
                          size="small"
                          onClick={() => openMpinDialog(row.apikeys_id, "revoke")}
                          color="primary"
                        >
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Permanently delete">
                        <IconButton
                          size="small"
                          onClick={() => openMpinDialog(row.apikeys_id, "permanentDelete")}
                          sx={{ color: "error.main" }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Generate Confirmation Dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm API Key Generation</DialogTitle>

        <DialogContent>
          <Typography sx={{ fontSize: "0.85rem" }}>
            You are generating a new API key for{" "}
            <strong>{selectedFunctionName || "the selected service"}</strong>.
            <br />
            <br />
            You may need to update the API key wherever it's applied previously.
            <br />
            <br />
            <strong>Are you sure you want to continue?</strong>
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setConfirmOpen(false)}
            variant="contained"
            size="small"
          >
            No
          </Button>

          <Button
            onClick={() => {
              setConfirmOpen(false);
              handleGenerateKey();
            }}
            size="small"
            disabled={isGenerating}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete type chooser — soft (temporary) vs permanent */}
      <Dialog
        open={Boolean(deleteChoiceRowId)}
        onClose={closeDeleteChoice}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete API Key</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.85rem", mb: 2 }}>
            How would you like to delete this API key?
          </Typography>
          <Stack spacing={1.5}>
            <Button
              variant="outlined"
              color="warning"
              startIcon={<AccessTimeIcon />}
              onClick={() => chooseDeleteType("delete")}
              sx={{ justifyContent: "flex-start", textAlign: "left", py: 1 }}
            >
              <Box>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                  Temporary (Soft) Delete
                </Typography>
                <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }}>
                  Moves it to Inactive Keys — you can revoke it later.
                </Typography>
              </Box>
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteForeverIcon />}
              onClick={() => chooseDeleteType("permanentDelete")}
              sx={{ justifyContent: "flex-start", textAlign: "left", py: 1 }}
            >
              <Box>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                  Permanent Delete
                </Typography>
                <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }}>
                  Removes it for good. This cannot be undone.
                </Typography>
              </Box>
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteChoice} size="small">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* MPIN Confirmation Dialog — shared by Delete / Revoke / Permanent Delete / View */}
      <Dialog
        open={Boolean(mpinDialog)}
        onClose={closeMpinDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{currentConfig?.label} API Key</DialogTitle>

        <DialogContent>
          <Typography sx={{ fontSize: "0.85rem", mb: 2 }}>
            Please enter your MPIN to confirm you want to{" "}
            <strong>{currentConfig?.confirmText}</strong>.
          </Typography>

          <TextField
            label="MPIN"
            type="password"
            fullWidth
            size="small"
            value={mpin}
            onChange={(e) => {
              setMpin(e.target.value);
              if (mpinError) setMpinError("");
            }}
            error={Boolean(mpinError)}
            helperText={mpinError || " "}
            inputProps={{ maxLength: 6, inputMode: "numeric" }}
            autoFocus
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={closeMpinDialog} variant="contained" size="small">
            Cancel
          </Button>

          <Button
            onClick={handleConfirmAction}
            color={currentConfig?.buttonColor || "primary"}
            variant="outlined"
            size="small"
            disabled={isActionBusy}
          >
            {isActionBusy ? <CircularProgress size={18} /> : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reveal API Key Dialog — shown after a successful PIN check */}
      <Dialog
        open={Boolean(revealedKey)}
        onClose={() => setRevealedKey(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>API Key</DialogTitle>

        <DialogContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              backgroundColor: "#f5f5f5",
              p: 1.5,
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" sx={{ wordBreak: "break-all", flex: 1 }}>
              {revealedKey}
            </Typography>
            <Tooltip title="Copy">
              <IconButton size="small" onClick={() => handleCopy(revealedKey)}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setRevealedKey(null)} variant="contained" size="small">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageApiKey;